import { motion } from 'framer-motion';
import type { PlayerId, Player, CardInstance } from '../../types/kotch';
import { CARD_DEFINITIONS } from '../../data/kotch/cards';
import { FACTION_ABILITIES } from '../../data/kotch/factionDecks';
import Card from './Card';

interface FactionAbilitySelectProps {
  pendingAbility: {
    type: 'discard_opponent_hand';
    winnerId: PlayerId;
    loserId: PlayerId;
    eligibleCardIds: string[];
  };
  players: Record<PlayerId, Player>;
  onSelectTarget: (cardInstanceId: string) => void;
}

export default function FactionAbilitySelect({
  pendingAbility,
  players,
  onSelectTarget,
}: FactionAbilitySelectProps) {
  const winner = players[pendingAbility.winnerId];
  const loser = players[pendingAbility.loserId];
  const eligibleSet = new Set(pendingAbility.eligibleCardIds);

  return (
    <motion.div
      className="kotch-faction-ability-select"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="kotch-faction-ability-header">
        <h2>{winner.faction ? FACTION_ABILITIES[winner.faction].name : 'Faction Ability'}</h2>
        <p>
          {winner.name} won the center lane! Choose a card to discard from {loser.name}'s hand.
        </p>
        <p className="kotch-faction-ability-hint">
          Special cards can only be discarded if they were revealed.
        </p>
      </div>

      <div className="kotch-faction-ability-cards">
        {loser.hand.map((card: CardInstance) => {
          const isEligible = eligibleSet.has(card.instanceId);
          const def = CARD_DEFINITIONS[card.definitionId];
          const isSpecialLocked = def && def.category === 'special' && !isEligible;

          return (
            <div
              key={card.instanceId}
              className={`kotch-faction-ability-card-wrapper ${isEligible ? 'eligible' : 'ineligible'}`}
            >
              <Card
                card={card}
                faceUp={true}
                disabled={!isEligible}
                onClick={isEligible ? () => onSelectTarget(card.instanceId) : undefined}
                size="medium"
                playerFaction={loser.faction}
              />
              {isSpecialLocked && (
                <div className="kotch-faction-ability-locked">
                  Not revealed
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
